import React, { useState, useEffect } from 'react';
import {
  Select,
  MenuItem,
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import BluetoothConnect from '../utils/BluetoothConnect';
import AppBridge from '../utils/AppBridge';

const Header = ({
  profiles,
  currentProfileIndex,
  currentChannelIndex,
  onProfileChange,
  onChannelChange,
  connectedDevice,
  setConnectedDevice,
  deviceInfo,
  isMobile,
  onPageChange,
  arduinoVersion,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [bluetoothDialogOpen, setBluetoothDialogOpen] = useState(false);

  const selectedProfile = profiles[currentProfileIndex];
  const selectedChannel = selectedProfile?.channels[currentChannelIndex];

  const toggleMenu = (open) => () => {
    setMenuOpen(open);
  };

  const handleConnect = () => {
    if (isMobile) {
      handleNativeScan();
    } else {
      setBluetoothDialogOpen(true);
    }
  };

  const handleDeviceSelect = (device) => {
    setConnectedDevice(device);
    console.log('Connected to:', device.name);

    // Listen for disconnection (web only)
    if (!isMobile && typeof device.addEventListener === 'function') {
      device.addEventListener('gattserverdisconnected', handleDisconnect);
    }
  };

  // Function to trigger native BLE scanning
  const handleNativeScan = () => {
    AppBridge.sendMessage('bluetooth_scan');
  };

  const handleDisconnect = () => {
    console.log('Device disconnected');
    setConnectedDevice(null);
  };

  const handleManualDisconnect = () => {
    if (connectedDevice) {
      if (isMobile) {
        // Send disconnect request to native
        AppBridge.sendMessage('bluetooth_disconnect');
      } else if (connectedDevice.gatt) {
        connectedDevice.gatt.disconnect();
        handleDisconnect();
      }
    }
  };

  // Listen for Bluetooth-related messages from native
  useEffect(() => {
    const handleBluetoothMessage = (event) => {
      const data = event.detail;
      if (data.type === 'bluetooth_connected') {
        setConnectedDevice(data.device); // Expect { name: 'bt05', ... } from native
        console.log('Native connected to:', data.device.name);
      } else if (data.type === 'bluetooth_disconnected') {
        setConnectedDevice(null);
      }
    };

    window.addEventListener('bluetooth_message', handleBluetoothMessage);

    return () => {
      window.removeEventListener('bluetooth_message', handleBluetoothMessage);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (
        connectedDevice &&
        !isMobile &&
        typeof connectedDevice.removeEventListener === 'function'
      ) {
        connectedDevice.removeEventListener(
          'gattserverdisconnected',
          handleDisconnect
        );
      }
    };
  }, [connectedDevice, isMobile]);

  return (
    <>
      <AppBar
        position="static"
        color="primary"
        sx={{
          padding: '2px 6px',
          minHeight: '44px',
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'nowrap',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            minHeight: '44px',
          }}
        >
          {/* LED Channel Selector + Width/Height */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              flex: 1,
            }}
          >
            <Select
              value={currentChannelIndex}
              onChange={(e) => onChannelChange(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: '#fff',
                fontSize: '12px',
                minWidth: '90px',
                height: '32px',
              }}
            >
              {selectedProfile?.channels.map((channel, index) => (
                <MenuItem key={index} value={index}>
                  {channel.name} ({channel.ledCount} LEDs)
                </MenuItem>
              ))}
            </Select>

            {/* Width & Height Display */}
            {selectedChannel && (
              <Typography
                variant="body2"
                sx={{
                  color: '#fff',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                W: {selectedChannel.width}, H: {selectedChannel.height}
              </Typography>
            )}
          </Box>

          {/* Hamburger Menu */}
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={toggleMenu(true)}
            sx={{
              fontSize: '20px',
              padding: '5px',
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Pop-Out Menu */}
      <Drawer anchor="right" open={menuOpen} onClose={toggleMenu(false)}>
        <Box
          sx={{
            width: 250,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
          role="presentation"
        >
          {/* Main list area */}
          <List sx={{ flex: '1 1 auto' }}>
            {/* Profile Selector */}
            <ListItem>
              <Select
                value={currentProfileIndex}
                onChange={(e) => {
                  onProfileChange(e.target.value);
                  setMenuOpen(false);
                }}
                variant="outlined"
                size="small"
                fullWidth
              >
                {profiles.map((profile, index) => (
                  <MenuItem key={index} value={index}>
                    {profile.name}
                  </MenuItem>
                ))}
              </Select>
            </ListItem>

            <Divider />

            {/* Bluetooth Connect/Disconnect Button */}
            <ListItem>
              {connectedDevice ? (
                <Button
                  variant="contained"
                  fullWidth
                  color="error"
                  onClick={handleManualDisconnect}
                  sx={{
                    fontWeight: 'bold',
                    padding: '10px',
                    fontSize: '14px',
                  }}
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  onClick={() => {
                    handleConnect();
                    setMenuOpen(false);
                  }}
                  sx={{
                    fontWeight: 'bold',
                    padding: '10px',
                    fontSize: '14px',
                  }}
                >
                  Connect
                </Button>
              )}
            </ListItem>

            {connectedDevice && (
              <ListItem
                sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
              >
                <ListItemText primary={`Connected: ${connectedDevice.name}`} />
                <ListItemText primary={`Arduino Version: ${arduinoVersion}`} />
              </ListItem>
            )}
          </List>
          {isMobile && (
            <>
              <Divider sx={{ mt: 2, mb: 2 }} />
              <ListItem>
                <Button
                  variant="outlined"
                  fullWidth
                  color="secondary"
                  onClick={() => {
                    handleNativeScan();
                    setMenuOpen(false);
                  }}
                  sx={{
                    fontWeight: 'bold',
                    padding: '10px',
                    fontSize: '14px',
                  }}
                >
                  Scan for Bluetooth Devices
                </Button>
              </ListItem>
            </>
          )}
          <ListItem>
            <Button
              variant="contained"
              fullWidth
              color="primary"
              onClick={() => {
                onPageChange('settings'); // ✅ switch to settings page
                setMenuOpen(false);
              }}
              sx={{
                fontWeight: 'bold',
                padding: '10px',
                fontSize: '14px',
              }}
            >
              Settings
            </Button>
          </ListItem>
          {/* Device info at the BOTTOM */}
          {deviceInfo && (
            <Box sx={{ flexShrink: 0, p: 2, borderTop: '1px solid #ccc' }}>
              {isMobile && <p>Mobile Device found</p>}
              <Card
                variant="outlined"
                sx={{ borderRadius: 2, backgroundColor: '#f5f5f5' }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 'bold', color: 'primary.main' }}
                  >
                    Device Info
                  </Typography>
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body2">
                      <strong>Platform:</strong> {deviceInfo.platform}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Version:</strong> {deviceInfo.version}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Model:</strong> {deviceInfo.model}
                    </Typography>
                    <Typography variant="body2">
                      <strong>App Version:</strong> {deviceInfo.appVersion}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Build Number:</strong> {deviceInfo.buildNumber}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Bluetooth Connect Dialog (for web only) */}
      {!isMobile && (
        <BluetoothConnect
          open={bluetoothDialogOpen}
          onClose={() => setBluetoothDialogOpen(false)}
          onDeviceSelect={handleDeviceSelect}
        />
      )}
    </>
  );
};

export default Header;
